// utility functions for immutably updating nested editableData structures

// update a document field within a partida in a declaration
export const updateDocumentField = (editableData, declarationIndex, itemIndex, docIndex, field, value) => {
  if (!editableData || !Array.isArray(editableData)) {
    return editableData;
  }

  const currentDoc = editableData?.[declarationIndex]?.partidas?.[itemIndex]?.documentos?.[docIndex];
  
  // early return if document doesn't exist or value hasn't changed
  if (!currentDoc || currentDoc[field] === value) {
    return editableData;
  }

  return editableData.map((decl, dIdx) => 
    dIdx === declarationIndex ? {
      ...decl,
      partidas: decl.partidas.map((item, iIdx) => 
        iIdx === itemIndex ? {
          ...item,
          documentos: item.documentos.map((doc, docIdx) => 
            docIdx === docIndex ? { ...doc, [field]: value } : doc
          )
        } : item
      )
    } : decl
  );
};

// update a declaration-level document field
export const updateDeclarationDocumentField = (editableData, declarationIndex, docIndex, field, value) => {
  if (!editableData || !Array.isArray(editableData)) {
    return editableData;
  }

  const currentDoc = editableData?.[declarationIndex]?.documentos?.[docIndex];
  
  // early return if document doesn't exist or value hasn't changed
  if (!currentDoc || currentDoc[field] === value) {
    return editableData;
  }

  return editableData.map((decl, dIdx) => 
    dIdx === declarationIndex ? {
      ...decl,
      documentos: decl.documentos.map((doc, idx) => 
        idx === docIndex ? { ...doc, [field]: value } : doc
      )
    } : decl
  );
};

// update an embalaje (package) field within a partida (item) in a declaration
export const updateEmbalajeField = (editableData, declarationIndex, itemIndex, embalajeIndex, field, value) => {
  if (!editableData || !Array.isArray(editableData)) {
    return editableData;
  }

  const currentEmbalaje = editableData?.[declarationIndex]?.partidas?.[itemIndex]?.embalajes?.[embalajeIndex];
  
  // early return if embalaje doesn't exist or value hasn't changed
  if (!currentEmbalaje || currentEmbalaje[field] === value) {
    return editableData;
  }

  return editableData.map((decl, dIdx) => 
    dIdx === declarationIndex ? {
      ...decl,
      partidas: decl.partidas.map((item, iIdx) => 
        iIdx === itemIndex ? {
          ...item,
          embalajes: item.embalajes.map((embalaje, eIdx) => 
            eIdx === embalajeIndex ? { ...embalaje, [field]: value } : embalaje
          )
        } : item
      )
    } : decl
  );
};

// update a marca (mark) field within an embalaje
export const updateMarcaField = (editableData, declarationIndex, itemIndex, embalajeIndex, marcaIndex, value) => {
  if (!editableData || !Array.isArray(editableData)) {
    return editableData;
  }

  const currentMarca = editableData?.[declarationIndex]?.partidas?.[itemIndex]?.embalajes?.[embalajeIndex]?.marcas?.[marcaIndex];
  
  // early return if marca doesn't exist or value hasn't changed
  if (!currentMarca || currentMarca.marca === value) {
    return editableData;
  }

  return editableData.map((decl, dIdx) => 
    dIdx === declarationIndex ? {
      ...decl,
      partidas: decl.partidas.map((item, iIdx) => 
        iIdx === itemIndex ? {
          ...item,
          embalajes: item.embalajes.map((embalaje, eIdx) => 
            eIdx === embalajeIndex ? {
              ...embalaje,
              marcas: embalaje.marcas.map((marca, mIdx) => 
                mIdx === marcaIndex ? { ...marca, marca: value } : marca
              )
            } : embalaje
          )
        } : item
      )
    } : decl
  );
};

// update a declaration-level field
export const updateDeclarationField = (editableData, declarationIndex, field, value) => {
  if (!editableData || !Array.isArray(editableData)) {
    return editableData;
  }

  const currentValue = editableData[declarationIndex]?.[field];

  // early return if value hasn't changed
  if (currentValue === value ||
      (currentValue === null && value === '') || 
      (currentValue === '' && value === null)) {
    return editableData;
  }

  return editableData.map((decl, idx) => 
    idx === declarationIndex ? { ...decl, [field]: value } : decl
  );
};

// update a partida (item) field within a declaration
export const updatePartidaField = (editableData, declarationIndex, itemIndex, field, value) => {
  if (!editableData || !Array.isArray(editableData)) {
    return editableData;
  }

  const currentValue = editableData?.[declarationIndex]?.partidas?.[itemIndex]?.[field];

  // early return if value hasn't changed
  if (currentValue === value ||
      (currentValue === null && value === '') || 
      (currentValue === '' && value === null)) {
    return editableData;
  }

  return editableData.map((decl, dIdx) => 
    dIdx === declarationIndex ? {
      ...decl,
      partidas: decl.partidas.map((item, iIdx) => 
        iIdx === itemIndex ? { ...item, [field]: value } : item
      )
    } : decl
  );
};

// add a new partida (item) to a declaration
export const addPartida = (editableData, declarationIndex) => {
  if (!editableData || !Array.isArray(editableData)) {
    return editableData;
  }

  const declaration = editableData[declarationIndex];
  if (!declaration) {
    return editableData;
  }

  return editableData.map((decl, dIdx) => 
    dIdx === declarationIndex ? {
      ...decl,
      partidas: [
        ...(decl.partidas || []),
        {
          documentos: [],
          embalajes: [],
        }
      ]
    } : decl
  );
};

// remove a partida (item) from a declaration
export const removePartida = (editableData, declarationIndex, itemIndex) => {
  if (!editableData || !Array.isArray(editableData)) {
    return editableData;
  }

  return editableData.map((decl, dIdx) => 
    dIdx === declarationIndex ? {
      ...decl,
      partidas: (decl.partidas || []).filter((_, iIdx) => iIdx !== itemIndex)
    } : decl
  );
};

